/**
 * Three-state connectivity signal (ISS-445).
 *
 * `navigator.onLine` (NetworkDetector) only observes HARD disconnects — a
 * device with one bar reports `onLine: true` while every backend call times
 * out. This monitor adds the missing middle state:
 *
 *   'online'   — no evidence against the backend being reachable
 *   'degraded' — device believes it has a network, but the backend could not
 *                be reached (a read came back from cache, timed out, or a
 *                probe failed)
 *   'offline'  — hard disconnect (`navigator.onLine === false`)
 *
 * The signal is fed passively by callers reporting real read/write outcomes
 * (`reportBackendSuccess` / `reportBackendUnreachable`) — free, no extra
 * requests — and optionally confirmed by an active probe that re-checks on an
 * interval while degraded, so UIs can auto-recover the moment signal returns.
 */

export type ReachabilityState = 'online' | 'degraded' | 'offline';

type ReachabilityListener = (state: ReachabilityState) => void;

export interface ReachabilityMonitorOptions {
  /**
   * Active probe: resolve `true` if the backend answered. A rejection counts
   * as unreachable. Omit for a purely passive monitor.
   */
  probe?: () => Promise<boolean>;
  /** How often to re-probe while degraded. Default 10 seconds. */
  probeIntervalMs?: number;
}

const DEFAULT_PROBE_INTERVAL_MS = 10_000;

export class ReachabilityMonitor {
  private state: ReachabilityState;
  private readonly listeners = new Set<ReachabilityListener>();
  private readonly probe?: () => Promise<boolean>;
  private readonly probeIntervalMs: number;
  private probeTimer: ReturnType<typeof setInterval> | undefined;
  private readonly handleOnline: () => void;
  private readonly handleOffline: () => void;

  constructor(options: ReachabilityMonitorOptions = {}) {
    this.probe = options.probe;
    this.probeIntervalMs = options.probeIntervalMs ?? DEFAULT_PROBE_INTERVAL_MS;

    const hardOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;
    this.state = hardOnline ? 'online' : 'offline';

    this.handleOnline = () => {
      // Optimistic: the OS says a network came back. If the backend is still
      // unreachable, the next read/probe reports it and we drop to degraded.
      this.setState('online');
    };
    this.handleOffline = () => {
      this.setState('offline');
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('online', this.handleOnline);
      window.addEventListener('offline', this.handleOffline);
    }
  }

  getState(): ReachabilityState {
    return this.state;
  }

  /** Subscribe to state changes. Returns an unsubscribe function. */
  subscribe(callback: ReachabilityListener): () => void {
    this.listeners.add(callback);
    return () => {
      this.listeners.delete(callback);
    };
  }

  /** A call genuinely reached the backend. */
  reportBackendSuccess(): void {
    this.setState('online');
  }

  /**
   * A call could NOT reach the backend (cache-served read, timeout, transport
   * error). Ignored during hard offline — that state is already stronger.
   */
  reportBackendUnreachable(): void {
    if (this.state === 'offline') return;
    this.setState('degraded');
  }

  /**
   * Run the probe immediately and apply the result. Without a configured
   * probe this is a no-op that resolves to the current state.
   */
  async checkNow(): Promise<ReachabilityState> {
    if (!this.probe) return this.state;
    let reachable = false;
    try {
      reachable = await this.probe();
    } catch {
      reachable = false;
    }
    if (reachable) {
      this.setState('online');
    } else if (this.state !== 'offline') {
      this.setState('degraded');
    }
    return this.state;
  }

  destroy(): void {
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', this.handleOnline);
      window.removeEventListener('offline', this.handleOffline);
    }
    this.stopProbeLoop();
    this.listeners.clear();
  }

  private setState(next: ReachabilityState): void {
    if (next === this.state) {
      // Entering degraded repeatedly must still keep the loop alive, but a
      // no-change report never re-notifies subscribers.
      if (next === 'degraded') this.startProbeLoop();
      return;
    }
    this.state = next;
    if (next === 'degraded') {
      this.startProbeLoop();
    } else {
      this.stopProbeLoop();
    }
    this.listeners.forEach((fn) => fn(next));
  }

  private startProbeLoop(): void {
    if (!this.probe || this.probeTimer !== undefined) return;
    this.probeTimer = setInterval(() => {
      void this.checkNow();
    }, this.probeIntervalMs);
  }

  private stopProbeLoop(): void {
    if (this.probeTimer !== undefined) {
      clearInterval(this.probeTimer);
      this.probeTimer = undefined;
    }
  }
}
